import { LineItemModel } from 'Models';
import { LINE_ITEM_PRICE_MODEL } from 'common/enums/lineItemPriceModel';
import { PRODUCT_PRICE_MODEL } from 'common/enums/productPriceModel';
import bindMultiple from 'common/helpers/bindMultiple';
import isNumeric from 'common/helpers/isNumeric';
import config from '../../config';
import { AuthContextModel } from '../../Context';

export default class CommUnityLineItemModel extends LineItemModel {
  static parseTieredPrices(tieredPricingRawString) {
    const result = {};
    const tieredPrices = tieredPricingRawString.trim().split(',');

    for (let i = 0; i < tieredPrices.length; i++) {
      const chunks = tieredPrices[i].split(':');

      // Skip tieredPrice if number of chunks are not equal to two (key/value)
      if (chunks.length !== 2) {
        continue;
      }

      const key = String(chunks[0]).trim();

      // Skip pair if key is empty
      if (key.length === 0) {
        continue;
      }

      // Skip pair if value is not a valid Number
      if (!isNumeric(chunks[1])) {
        continue;
      }

      // Force convert String into Number
      const value = Number(chunks[1]);

      result[key] = value;
    }

    return result;
  }

  /**
   * @param {number} price
   * @param {number} discount
   * @returns {number}
   */
  static calcDiscountPercentage(price, discount) {
    return price ? (discount / price) * 100 : 0;
  }

  constructor(higherOrderClassInstance) {
    super(higherOrderClassInstance);

    /** @type {string} */
    this._priceModelSelected = LINE_ITEM_PRICE_MODEL.RECURRING;
    /** @type {number} */
    this._recurringPrice = 0;

    this._tieredPrices = null;
    this._tieredRecurringPrices = null;

    /** @type {number} */
    this.discount = 0;
    /** @type {number} */
    this.recurringDiscount = 0;

    bindMultiple(this, this.onPriceModelToggle);
  }

  /**
   * @returns {string}
   */
  get priceModelSelected() {
    switch (this.priceModel) {
      case PRODUCT_PRICE_MODEL.PER_CATEGORY:
        throw new Error('priceModel should not be PRODUCT_PRICE_MODEL.PER_CATEGORY');
      case PRODUCT_PRICE_MODEL.RECURRING:
        return LINE_ITEM_PRICE_MODEL.RECURRING;
      case PRODUCT_PRICE_MODEL.NON_RECURRING:
        return LINE_ITEM_PRICE_MODEL.NON_RECURRING;
      default:
        return this._priceModelSelected;
    }
  }

  set priceModelSelected(value) {
    if (this.priceModel !== PRODUCT_PRICE_MODEL.ALL) return;
    this._priceModelSelected = value;
  }

  /**
   * @returns {boolean}
   */
  get priceModelSelectedEditable() {
    return this.priceModel === PRODUCT_PRICE_MODEL.ALL;
  }

  onPriceModelToggle() {
    if (this.priceModel !== PRODUCT_PRICE_MODEL.ALL) return;
    this._priceModelSelected =
      this._priceModelSelected === LINE_ITEM_PRICE_MODEL.RECURRING
        ? LINE_ITEM_PRICE_MODEL.NON_RECURRING
        : LINE_ITEM_PRICE_MODEL.RECURRING;

    this.modelChanged(this);
  }

  get upfrontDiscountPercentage() {
    return CommUnityLineItemModel.calcDiscountPercentage(this.dealerNet, this.discount);
  }

  get recurringDiscountPercentage() {
    return CommUnityLineItemModel.calcDiscountPercentage(this.recurringPrice, this.recurringDiscount);
  }

  /**
   * @returns {number}
   */
  get upfrontAdjusted() {
    return this.dealerNet - this.discount;
  }

  /**
   * @returns {number}
   */
  get upfrontExtended() {
    return this.quantity * this.upfrontAdjusted;
  }

  /**
   * @returns {number}
   */
  get recurringAdjusted() {
    return this.recurringPrice - this.recurringDiscount;
  }

  /**
   * @returns {number}
   */
  get recurringExtended() {
    return this.quantity * this.recurringAdjusted;
  }

  get total() {
    return this.priceModelSelected === LINE_ITEM_PRICE_MODEL.RECURRING ? this.recurringExtended : this.upfrontExtended;
  }

  get upfrontTotal() {
    return this.priceModelSelected === LINE_ITEM_PRICE_MODEL.NON_RECURRING ? this.upfrontExtended : 0;
  }

  get recurringTotal() {
    return this.priceModelSelected === LINE_ITEM_PRICE_MODEL.RECURRING ? this.recurringExtended : 0;
  }

  _getTieredPrice({ tieredPricesMapPropertyName, flagName }) {
    const flagRawValue = this.getFlagValue(flagName);

    if (typeof flagRawValue !== 'string' || !flagRawValue) {
      return false;
    }

    let activePriceTiers = this.masterOrder.calcResults.getValue('activePriceTiers');

    if (!activePriceTiers) {
      return false;
    }

    if (this[tieredPricesMapPropertyName] === null) {
      this[tieredPricesMapPropertyName] = CommUnityLineItemModel.parseTieredPrices(flagRawValue);
    }

    activePriceTiers = activePriceTiers.split(',');

    for (let i = 0; i < activePriceTiers.length; i++) {
      const tier = activePriceTiers[i].trim();

      if (typeof this[tieredPricesMapPropertyName][tier] === 'number') {
        return this[tieredPricesMapPropertyName][tier];
      }
    }

    if (config.showConsoleLog) {
      console.warn('Unable to find a tiered pricing for line item', this);
    }

    return false;
  }

  /**
   * @returns {number}
   */
  get dealerNet() {
    const tieredPrice = this._getTieredPrice({
      tieredPricesMapPropertyName: '_tieredPrices',
      flagName: 'tieredPrices',
    });

    return typeof tieredPrice === 'number' ? tieredPrice : super.dealerNet;
  }

  set dealerNet(value) {
    super.dealerNet = value;
  }

  /**
   * @returns {number}
   */
  get recurringPrice() {
    const tieredPrice = this._getTieredPrice({
      tieredPricesMapPropertyName: '_tieredRecurringPrices',
      flagName: 'tieredRecurringPrices',
    });

    return typeof tieredPrice === 'number' ? tieredPrice : this._recurringPrice;
  }

  set recurringPrice(value) {
    this._recurringPrice = value;
  }

  get isDiscountDisabled() {
    if (!AuthContextModel.hasInternalPermissions) {
      return true;
    }

    const flagValue = this.getFlagValue('disableDiscount');

    if (flagValue === 'FORCE') {
      return true;
    }

    return flagValue === 'TRUE' && !AuthContextModel.hasSalesOpsPermissions;
  }

  get isRecurringDiscountDisabled() {
    if (!AuthContextModel.hasInternalPermissions) {
      return true;
    }

    const flagValue = this.getFlagValue('disableRecurringDiscount');

    if (flagValue === 'FORCE') {
      return true;
    }

    return flagValue === 'TRUE' && !AuthContextModel.hasSalesOpsPermissions;
  }
}
